// Screen: 01-entry-invitation — hero card shown before Accept/Decline

import { GlassCard } from '@/shared/ui/GlassCard';
import { MonoAmount } from '@/shared/ui/MonoAmount';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import type { InviteDetailOut } from './types';
import { useT } from '@/shared/lib/i18n';

interface Props {
  invite: InviteDetailOut;
}

function periodLabel(period: { interval: number; unit: string }, t: ReturnType<typeof useT>): string {
  const unit = period.unit;
  if (unit === 'weeks') {
    return period.interval === 1
      ? t('invitation.periodWeek')
      : t('invitation.periodWeeks', { count: period.interval });
  }
  if (unit === 'months') {
    if (period.interval === 1) return t('invitation.periodMonth');
    if (period.interval === 3) return t('invitation.periodQuarter');
    if (period.interval === 12) return t('invitation.periodYear');
    return t('invitation.periodMonths', { count: period.interval });
  }
  return t('invitation.periodCustom', { count: period.interval, unit });
}

function splitLabel(splitMode: string, t: ReturnType<typeof useT>): string {
  if (splitMode === 'equal') return t('invitation.splitEqual');
  if (splitMode === 'proportional') return t('invitation.splitProportional');
  if (splitMode === 'fixed') return t('invitation.splitFixed');
  return splitMode;
}

function privacyLabel(privacy: string, memberCount: number, t: ReturnType<typeof useT>): string {
  if (privacy === 'everyone') {
    return memberCount === 1
      ? t('invitation.privacyOneEveryone')
      : t('invitation.privacyEveryone', { count: memberCount });
  }
  if (privacy === 'count_only') {
    return memberCount === 1
      ? t('invitation.privacyOneCountOnly')
      : t('invitation.privacyCountOnly', { count: memberCount });
  }
  if (privacy === 'nobody') return t('invitation.privacyNobody');
  return privacy;
}

function deadlineLabel(shift: number, t: ReturnType<typeof useT>): string {
  if (shift === 0) return t('invitation.deadlineSame');
  if (shift > 0) {
    return shift === 1
      ? t('invitation.deadlineOneAfter')
      : t('invitation.deadlineAfter', { count: shift });
  }
  return t('invitation.deadlineBeforeMonthly', { day: Math.abs(shift) });
}

interface DetailRowProps {
  label: string;
  value: string;
  last?: boolean;
}

function DetailRow({ label, value, last }: DetailRowProps) {
  return (
    <div
      className={`flex justify-between items-center py-[11px] text-[14px] ${
        !last ? 'border-b border-black/[0.08]' : ''
      }`}
    >
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export function InvitationCard({ invite }: Props) {
  const t = useT();
  const letter = invite.subscription_name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = invite.subscription_icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(invite.subscription_icon);
  const isSlug = invite.subscription_icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(invite.subscription_icon);

  return (
    <div className="flex flex-col gap-3">
      {/* Hero card */}
      <GlassCard level="hero" className="p-[22px]">
        <p className="text-[13px] text-text-secondary">{t('invitation.invitedTo')}</p>

        <div className="flex items-center gap-3 mt-[3px] mb-1">
          {isEmoji ? (
            <span className="w-[44px] h-[44px] rounded-[12px] bg-brand-gradient flex items-center justify-center text-[21px] font-bold flex-shrink-0 shadow-[0_3px_8px_rgba(18,120,90,0.28)]">
              {invite.subscription_icon}
            </span>
          ) : isSlug ? (
            <img
              src={`/api/v1/icons/${invite.subscription_icon}/default`}
              alt={invite.subscription_name}
              className="w-[44px] h-[44px] rounded-[12px] object-contain bg-white/60 border border-white/60 p-1 flex-shrink-0"
            />
          ) : (
            <SubscriptionIcon letter={letter} size="lg" />
          )}
          <span className="text-[32px] font-bold tracking-[-0.01em] leading-tight">
            {invite.subscription_name}
          </span>
        </div>

        <p className="text-[14px] text-text-secondary">
          {t('invitation.fromHolder', { name: invite.holder_display_name })}
        </p>

        <div className="flex items-baseline gap-2 mt-4">
          <MonoAmount
            value={`${Number(invite.total_price).toLocaleString('ru')} ${invite.currency === 'RUB' ? '₽' : invite.currency}`}
            variant="gradient"
            className="text-[42px] font-bold leading-none"
          />
          <span className="text-[15px] text-text-secondary">
            {periodLabel(invite.period, t)}
          </span>
        </div>
      </GlassCard>

      {/* Detail rows */}
      <GlassCard level="content" className="px-[18px] py-[6px]">
        <DetailRow label={t('invitation.payBy')} value={deadlineLabel(invite.deadline_shift, t)} />
        <DetailRow label={t('invitation.split')} value={splitLabel(invite.split_mode, t)} />
        <DetailRow
          label={t('invitation.youWillSee')}
          value={privacyLabel(invite.privacy, invite.member_count, t)}
        />
        <DetailRow
          label={t('invitation.pastHistory')}
          value={invite.show_history ? t('invitation.visible') : t('invitation.hidden')}
          last
        />
      </GlassCard>
    </div>
  );
}
